<template>
    <div class="trajectory-header" :class="{ 'desktop-trajectory-header': desktop, 'trajectory-header-embedded': embedded }">
        <div class="path-icon">
            <div class="current-location-icon">
                <div class="outer-circle" />
                <div class="inner-circle" />
            </div>
            <div class="ellipsis-container">
                <div class="ellipsis" />
                <div class="ellipsis" />
                <div class="ellipsis" />
            </div>
            <i :class="[fontAwesomePrefix, 'fa-location-dot']" class="f-12" />
        </div>
        <div class="path-fields">
            <div class="field line-clamp-1" :class="{ clickable: interactive }" @click="selectStart">
                <div class="value" :class="{ placeholder: startPlaceholder, 'current-location-label': startHighlighted }">
                    <slot name="start">{{ startValue }}</slot>
                </div>
            </div>
            <div class="field line-clamp-1" :class="{ clickable: interactive }" @click="selectEnd">
                <div class="value" :class="{ placeholder: endPlaceholder }">
                    <slot name="end">{{ endValue }}</slot>
                </div>
            </div>
        </div>
        <div v-if="$slots.actions" class="actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<script>
import MapMixin from "@/helpers/maps/map-mixin.js";

export default {
    name: `map-mobile-trajectory-header`,
    mixins: [MapMixin],
    props: {
        startValue: { type: String, default: `` },
        endValue: { type: String, default: `` },
        startPlaceholder: { type: Boolean, default: false },
        endPlaceholder: { type: Boolean, default: false },
        startHighlighted: { type: Boolean, default: false },
        desktop: { type: Boolean, default: false },
        embedded: { type: Boolean, default: false },
        interactive: { type: Boolean, default: true }
    },
    methods: {
        // Open the start selector from the shared header.
        selectStart() {
            if (!this.interactive) {
                return;
            }

            this.$emit(`select-start`);
        },
        // Open the end selector from the shared header.
        selectEnd() {
            if (!this.interactive) {
                return;
            }

            this.$emit(`select-end`);
        }
    }
};
</script>

<style lang="scss" scoped>
.trajectory-header {
    pointer-events: auto;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    max-width: 100svw;

    background: white;
    padding: 10px 12px;

    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

    &.desktop-trajectory-header {
        position: static;
        padding: 12px 0;
        margin-block: 12px;
        width: auto;
        background: none;
        box-shadow: none;
    }

    &.trajectory-header-embedded {
        position: static;
        max-width: none;
        box-shadow: none;
    }
}

.path-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: var(--color-muted);
    font-size: 12px;
    padding-top: 2px;

    .current-location-icon {
        position: relative;
        width: 24px;
        height: 24px;

        .outer-circle {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: #dfecff;
            border-radius: 50%;
        }

        .inner-circle {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 14px;
            height: 14px;
            background: var(--color-accent);
            border: 2px solid #fff;
            border-radius: 50%;
        }
    }

    .ellipsis-container {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .ellipsis {
            width: 4px;
            height: 4px;
            background: var(--border-color);
            border-radius: 50%;
        }
    }
}

.path-fields {
    display: flex;
    flex-direction: column;
    text-align: left;
    gap: 2px;
    min-width: 0;
    overflow: hidden;

    .field {
        position: relative;
        padding: 10px 16px;
        border-radius: 16px 16px 0 0;
        background: var(--color-light);
        min-width: 0;
        overflow: hidden;

        &.clickable {
            cursor: pointer;
        }
    }

    .field:nth-child(2) {
        border-radius: 0 0 16px 16px;
    }

    .value {
        color: var(--color-dark);
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;

        &.placeholder {
            color: var(--color-muted);
        }

        &.current-location-label {
            color: var(--color-accent);
        }
    }
}

.actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
}

:deep(.actions .b-dropdown .btn) {
    border-radius: 999px;
}

.desktop-trajectory-header {
    .path-fields .field .value {
        font-size: 11pt;
    }
}
</style>
